import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { BaseEndpoint } from './base.endpoint';

export type CreateSubscriptionRequestPayload = {
  priceId: string;
  customerId?: string | undefined;
  paymentMethodId: string;
  email?: string;
};

export type CreateSubscriptionResponsePayload = {
  subscriptionId: string;
  customerId?: string | undefined;
  clientSecret: string;
};
class StripeSecureEndpoint extends BaseEndpoint {
  private static instance: StripeSecureEndpoint;

  private constructor() {
    super('client');
  }

  static getInstance(): StripeSecureEndpoint {
    if (!StripeSecureEndpoint.instance) {
      StripeSecureEndpoint.instance = new StripeSecureEndpoint();
    }
    return StripeSecureEndpoint.instance;
  }

  async createSubscription(
    organizationId: string,
    data: CreateSubscriptionRequestPayload
  ): Promise<CreateSubscriptionResponsePayload> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organizationId}/create-subscription`,
        data,
        { headers }
      );

      logger.info('Subscription created successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create subscription:', error);
      return ApiErrorHandler.handle(error);
    }
  }
  async cancelSubscription(organisationId: string, subscriptionId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/cancel-subscription`,
        { subscriptionId },
        { headers }
      );

      logger.info('Subscription cancelled successfully');
      return response.data.data;
    } catch (error) {
      logger.error('Failed to cancel subscription:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getSubscription(organisationId: string, stripeCustomerId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<any>(
        `${this.baseUrl}/organisation/${organisationId}/get-subscription/${stripeCustomerId}`,
        { headers }
      );

      logger.info('Subscription fetched successfully');
      return response.data[0];
    } catch (error) {
      logger.error('Failed to fetch subscription:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async buyCredits(organisationId: string, customerId: string, priceId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/buy-credits`,
        { customerId, priceId },
        { headers }
      );
      console.log('Buy credits response:', response);

      logger.info('Buy credits URL generated successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to generate buy credits session:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getCustomerCardDetails(organisationId: string, stripeCustomerId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<any>(
        `${this.baseUrl}/organisation/${organisationId}/get-customer-cards/${stripeCustomerId}`,
        { headers }
      );
      logger.info('Customer card details fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch customer card details:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  // get customer billing history
  async getCustomerBillingHistory(organisationId: string, stripeCustomerId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<any>(
        `${this.baseUrl}/organisation/${organisationId}/get-billing-history/${stripeCustomerId}`,
        { headers }
      );
      logger.info('Customer billing history fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch customer billing history:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async getCustomerAddonHistory(organisationId: string, stripeCustomerId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<any>(
        `${this.baseUrl}/organisation/${organisationId}/get-addon-history/${stripeCustomerId}`,
        { headers }
      );
      logger.info('Customer addon history fetched successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch customer addon history:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async startImmediateSubscription(
    organisationId: string,
    subscriptionId: string,
    priceId: string
  ): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/start-immediate-subscription`,
        { subscriptionId, priceId },
        { headers }
      );
      logger.info('Immediate subscription started successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to start immediate subscription:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async cancelAtPeriodEnd(organisationId: string, subscriptionId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/cancel-at-period-end`,
        { subscriptionId },
        { headers }
      );
      logger.info('Subscription cancel at period end set successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to set cancel at period end:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async replaceCustomerCard(
    organisationId: string,
    stripeCustomerId: string,
    newPaymentMethodId: string,
    oldPaymentMethodId: string
  ): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/replace-customer-card`,
        { customerId: stripeCustomerId, newPaymentMethodId, oldPaymentMethodId },
        { headers }
      );
      logger.info('Customer card replaced successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to replace customer card:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async renewSubscription(organisationId: string, subscriptionId: string): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/renew-cancelled-subscription`,
        { subscriptionId },
        { headers }
      );
      logger.info('Subscription renewed successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to renew subscription:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createRenewalCheckoutSession(
    organisationId: string,
    customerId: string,
    email: string,
    firstName: string,
    planId: string,
    isTrialEnabled: boolean,
    companyName: string
  ): Promise<{ url: string }> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/create-renewal-checkout`,
        {
          customerId,
          email,
          firstName,
          planId,
          isTrailEnabled: isTrialEnabled,
          clientId: organisationId,
          companyName,
        },
        { headers }
      );
      logger.info('Renewal checkout session created successfully', response);
      const checkoutUrl = response.data;
      console.log('checkoutUrl', checkoutUrl);
      return checkoutUrl;
    } catch (error) {
      logger.error('Failed to create renewal checkout session:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createPaymentMethod(
    organisationId: string,
    stripeCustomerId: string,
    paymentMethodId: string
  ): Promise<any> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<any>(
        `${this.baseUrl}/organisation/${organisationId}/create-payment-method`,
        { customerId: stripeCustomerId, paymentMethodId },
        { headers }
      );
      logger.info('Payment method created and attached successfully');
      return response.data;
    } catch (error) {
      logger.error('Failed to create payment method:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const stripeSecureEndpoint = StripeSecureEndpoint.getInstance();
